import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type { DashboardEducationStatus } from "../../types/dashboard.types";
import styles from "./EducationStatusChart.module.scss";

/**
 * Her statü için net, birbirinden ayrık iki renkli gradient stopları.
 * ApexCharts fill.gradient'e `gradientToColors` ile verilir — ilk renk
 * `colors[i]`, biten renk `GRADIENTS[i][1]`.
 */
const GRADIENTS: [string, string][] = [
  ["#22c55e", "#4ade80"], // yeşil
  ["#3b82f6", "#60a5fa"], // mavi
  ["#8b5cf6", "#a78bfa"], // mor
  ["#f59e0b", "#fbbf24"], // amber
  ["#ef4444", "#f87171"], // kırmızı
  ["#06b6d4", "#22d3ee"], // cyan
  ["#64748b", "#94a3b8"], // gri
  ["#ec4899", "#f472b6"], // pembe
];

interface EducationStatusChartProps {
  title: string;
  data: DashboardEducationStatus | null;
  totalLabel?: string;
}

const EducationStatusChart: React.FC<EducationStatusChartProps> = ({
  title,
  data,
  totalLabel,
}) => {
  const { t } = useTranslation();
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const series = useMemo(() => items.map((i) => i.count), [items]);
  const labels = useMemo(() => items.map((i) => i.label), [items]);
  const colors = useMemo(
    () => items.map((_, idx) => GRADIENTS[idx % GRADIENTS.length][0]),
    [items],
  );
  const gradientTo = useMemo(
    () => items.map((_, idx) => GRADIENTS[idx % GRADIENTS.length][1]),
    [items],
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        fontFamily: "Inter, sans-serif",
        animations: {
          enabled: true,
          speed: 600,
          animateGradually: { enabled: true, delay: 80 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
        dropShadow: {
          enabled: true,
          top: 4,
          left: 0,
          blur: 12,
          color: "#0f172a",
          opacity: 0.08,
        },
      },
      labels,
      colors,
      stroke: { width: 3, colors: ["#ffffff"] },
      fill: {
        type: "gradient",
        gradient: {
          type: "diagonal1",
          shade: "light",
          shadeIntensity: 0.4,
          opacityFrom: 1,
          opacityTo: 0.92,
          stops: [0, 100],
          gradientToColors: gradientTo,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "74%",
            background: "transparent",
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -8,
                fontSize: "12px",
                fontWeight: 500,
                color: "#707a8f",
              },
              value: {
                show: true,
                offsetY: 6,
                fontSize: "28px",
                fontWeight: 700,
                color: "#14181f",
                formatter: (val: string) => Number(val).toLocaleString("tr-TR"),
              },
              total: {
                show: true,
                showAlways: true,
                label: totalLabel ?? "Toplam",
                fontSize: "12px",
                fontWeight: 500,
                color: "#707a8f",
                formatter: () =>
                  total > 0 ? total.toLocaleString("tr-TR") : "0",
              },
            },
          },
          expandOnClick: false,
        },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      tooltip: {
        enabled: true,
        theme: "light",
        y: {
          formatter: (val: number) => val.toLocaleString("tr-TR"),
        },
      },
      states: {
        hover: { filter: { type: "lighten", value: 0.04 } },
        active: { filter: { type: "darken", value: 0.08 } },
      },
    }),
    [labels, colors, gradientTo, total, totalLabel],
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
      </div>

      <div className={styles.body}>
        <div className={styles.chartWrap}>
          {series.length > 0 ? (
            <Chart
              options={options}
              series={series}
              type="donut"
              height={300}
            />
          ) : (
            <div className={styles.empty}>Veri yok</div>
          )}
        </div>

        <ul className={styles.legend}>
          {items.map((item, idx) => {
            const pct = total > 0 ? (item.count / total) * 100 : 0;
            const [from, to] = GRADIENTS[idx % GRADIENTS.length];
            return (
              <li key={item.label} className={styles.legendItem}>
                <div className={styles.legendRow}>
                  <span
                    className={styles.legendDot}
                    style={{
                      background: `linear-gradient(135deg, ${from}, ${to})`,
                    }}
                    aria-hidden
                  />
                  <span className={styles.legendLabel}>{item.label}</span>
                  <span className={styles.legendCount}>
                    {item.count.toLocaleString("tr-TR")}
                  </span>
                </div>
                <div className={styles.legendBar}>
                  <div
                    className={styles.legendBarFill}
                    style={{
                      width: `${Math.min(100, pct)}%`,
                      background: `linear-gradient(90deg, ${from}, ${to})`,
                    }}
                  />
                </div>
                <span className={styles.legendPct}>
                  %{pct.toFixed(pct >= 10 ? 0 : 1)}
                </span>
              </li>
            );
          })}
          {items.length === 0 && (
            <li className={styles.legendEmpty}>{t("dashboard.noDataYet")}</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default EducationStatusChart;
